/**
 * /products — Customer-facing add-on catalog (OCP-114).
 *
 * Pulls active products from `GET /v1/portal/products` (public) +
 * customer's active subscriptions from `GET /v1/portal/me/subscriptions`
 * (cookie-auth) so the per-product Active-badge logic can render
 * "✓ Active" + "Manage" instead of "Buy" for already-subscribed items.
 *
 * Buy CTA → `POST /v1/portal/checkout/create` (Paddle hosted-checkout)
 * → window.location.assign(checkout_url). Manage CTA →
 * `/subscriptions/:id` (handled by OCP-115's page; for now we just
 * navigate — a 404 there is acceptable until that ticket lands).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { contractGateMessage } from '../components/BusinessCapacityGate';
import { formatMoney, useOrderReview } from '../components/OrderReviewStep';
import { fmtDate } from '../lib/format-time';
import { featuresToBullets, overageLine } from '../lib/product-features';
import { resetOmsSubscriptionCache } from '../lib/use-has-oms-subscription';
import { RoleReadOnlyNotice } from '../components/RoleReadOnlyNotice';
import { usePortalAccess } from '../lib/use-portal-access';
import { intlTag } from '../i18n';

interface CatalogProduct {
  id: string;
  display_name: string;
  product_family: string;
  price_eur_cents: number;
  billing_period: string;
  paddle_product_id: string | null;
  features: Record<string, unknown>;
  overage_config: Record<string, unknown> | null;
  description: string | null;
  marketing_bullets: string[] | null;
}
interface ActiveSubscription {
  id: string;
  product_id: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

type FamilyFilter = 'all' | 'orboto-saas' | 'orboto-self-host' | 'oms' | 'addons';

/** Tab order; labels live in the `products` catalogue under `filter.<key>` (OCP-656). */
const FILTERS: readonly FamilyFilter[] = ['all', 'orboto-saas', 'orboto-self-host', 'oms', 'addons'];

/** OCP-512: parse the `?family=` deep-link param; unknown/absent -> 'all'. */
function parseFamilyFilter(raw: string | null): FamilyFilter {
  return raw && (FILTERS as readonly string[]).includes(raw) ? (raw as FamilyFilter) : 'all';
}

/** Families with a catalogue label (`family.<key>`); anything else renders its raw key. */
const KNOWN_FAMILIES = new Set(['orboto-saas', 'orboto-self-host', 'oms', 'backup', 'other']);
/** Billing periods with a catalogue label (`period.<key>`). */
const KNOWN_PERIODS = new Set(['one-time', 'monthly', 'yearly', 'metered']);

interface InstanceRow {
  id: string;
}

/** OCP-1036 - the account's live company package offer (GET /v1/portal/company-package). */
interface CompanyPackageOffer {
  id: string;
  product_id: string;
  product_display_name: string;
  billing_period: string;
  seat_count: number;
  price_per_seat_cents: number;
  total_per_period_cents: number;
  currency: string;
  term_months: number;
  includes_onboarding: boolean;
  includes_training: boolean;
  customer_note: string | null;
  offer_expires_at: string;
}

export function ProductsPage() {
  const { t } = useTranslation('products');
  const { canConcludeContract } = usePortalAccess();
  const navigate = useNavigate();
  const [products, setProducts] = useState<CatalogProduct[] | null>(null);
  const [subs, setSubs] = useState<ActiveSubscription[] | null>(null);
  // OCP-213: tenant ownership for /products segmentation. Empty list
  // = no Cloud-tenant → customer is OMS-standalone audience. Non-empty
  // = SaaS-tenant customer; OMS tiles are framed as "use Mail in your
  // own products" (separate from the included tenant mail).
  const [tenantCount, setTenantCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OCP-1036 - a package agreed with this account; null = none (list price).
  const [companyPackage, setCompanyPackage] = useState<CompanyPackageOffer | null>(null);
  // OCP-512: the active tab is driven by the `?family=` param (single source of
  // truth), so a deep link like /products?family=oms lands on that tab and a tab
  // click is bookmarkable/shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseFamilyFilter(searchParams.get('family'));
  const setFilter = useCallback(
    (f: FamilyFilter) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (f === 'all') next.delete('family');
          else next.set('family', f);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [checkoutPending, setCheckoutPending] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setError(null);
    try {
      // Catalog is public; subscriptions + instances are auth'd. All
      // run in parallel — if subs/instances 401 (rare — visit page
      // right after session expiry) we still render the catalog
      // without Active-badges or segmentation framing.
      const [productsRes, subsRes, instancesRes, packageRes] = await Promise.allSettled([
        api<{ items: CatalogProduct[] }>('/v1/portal/products', { signal }),
        api<{ items: ActiveSubscription[] }>('/v1/portal/me/subscriptions', { signal }),
        api<{ items: InstanceRow[] }>('/v1/portal/instances', { signal }),
        api<{ company_package: CompanyPackageOffer | null }>('/v1/portal/company-package', { signal }),
      ]);
      setCompanyPackage(packageRes.status === 'fulfilled' ? (packageRes.value?.company_package ?? null) : null);
      if (productsRes.status === 'rejected') throw productsRes.reason;
      setProducts(productsRes.value.items);
      setSubs(subsRes.status === 'fulfilled' ? subsRes.value.items : []);
      setTenantCount(
        instancesRes.status === 'fulfilled' ? instancesRes.value.items.length : 0,
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError)
        setError(t('loadErrorHttp', { status: err.status }));
      else setError(t('loadError'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  // product_id → active subscription (so the card can branch on it).
  const activeByProduct = useMemo(() => {
    const out = new Map<string, ActiveSubscription>();
    for (const s of subs ?? []) out.set(s.product_id, s);
    return out;
  }, [subs]);

  const visible = useMemo(() => {
    if (!products) return [];
    if (filter === 'all') return products;
    if (filter === 'addons') {
      return products.filter(
        (p) => p.product_family !== 'orboto-saas' && p.product_family !== 'orboto-self-host',
      );
    }
    return products.filter((p) => p.product_family === filter);
  }, [products, filter]);

  // Group visible products by family for section-header rendering.
  const grouped = useMemo(() => {
    const map = new Map<string, CatalogProduct[]>();
    for (const p of visible) {
      const arr = map.get(p.product_family) ?? [];
      arr.push(p);
      map.set(p.product_family, arr);
    }
    return Array.from(map.entries());
  }, [visible]);

  // OCP-526 - the order review step: services, price, billing cycle and term
  // on OUR surface before the order is placed, with the ordering button
  // directly under that summary (AGB V4 1.2). It also hosts the OCP-524
  // responsible-contact capture as its first step when the account still owes
  // one, so the customer sees one dialog rather than two stacked ones.
  const orderReview = useOrderReview();

  const runBuy = useCallback(
    async (product: CatalogProduct, seatCount: number | null, discountCode: string | null) => {
      setCheckoutPending(product.id);
      setError(null);
      try {
        // Free products (price 0, no paddle_product_id) bypass Paddle —
        // checkout-create would reject them with 404 `product_not_purchasable`
        // since they have no Paddle SKU. Direct-activate instead.
        if (product.price_eur_cents === 0) {
          const r = await api<{
            subscription_id: string;
            redirect_url: string;
          }>('/v1/portal/subscriptions/activate-free', {
            method: 'POST',
            body: { product_id: product.id },
          });
          // OCP-222: invalidate the OMS-subscription cache so /mail/*
          // gating recomputes against the just-activated row instead
          // of serving the pre-activation `hasOms=false` from cache.
          if (product.product_family === 'oms') {
            resetOmsSubscriptionCache();
          }
          navigate(r.redirect_url);
          return;
        }
        const r = await api<{ checkout_url: string }>('/v1/portal/checkout/create', {
          method: 'POST',
          body: {
            product_id: product.id,
            ...(seatCount !== null ? { seat_count: seatCount } : {}),
            // OCP-991 - the code on the summary the customer confirmed.
            ...(discountCode ? { discount_code: discountCode } : {}),
          },
        });
        // Hand off to Paddle's hosted checkout. A full window.location
        // assign keeps the back-button sane: customer hits back from
        // Paddle, lands here on /products with a fresh load.
        window.location.assign(r.checkout_url);
      } catch (err) {
        setCheckoutPending(null);
        if (err instanceof ApiError && err.status === 401) {
          // Session expired between page-load + click. Bounce to login
          // with a return-URL.
          navigate('/login?from=' + encodeURIComponent('/products'));
          return;
        }
        if (err instanceof ApiError && err.status === 409) {
          const body = err.body as { error?: string } | null;
          if (body?.error === 'free_subscription_already_active') {
            setError(t('freeAlreadyActive'));
            return;
          }
        }
        // OCP-524 / OCP-534 - the Terms gates say WHY in customer-facing copy;
        // "HTTP 400" tells the customer nothing they can act on.
        const gate = contractGateMessage(err);
        if (gate) setError(gate);
        else if (err instanceof ApiError)
          setError(t('checkoutErrorHttp', { status: err.status }));
        else setError(t('checkoutError'));
      }
    },
    [navigate, t],
  );

  const onBuy = useCallback(
    (product: CatalogProduct) => {
      orderReview.start({
        productId: product.id,
        onOrder: ({ seatCount, discountCode }) => runBuy(product, seatCount, discountCode),
      });
    },
    [orderReview, runBuy],
  );

  const onManage = useCallback(
    (subscription: ActiveSubscription) => {
      navigate(`/subscriptions/${subscription.id}`);
    },
    [navigate],
  );

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
      </div>
      <RoleReadOnlyNotice scope="contract" />

      {companyPackage && (
        <CompanyPackageCard
          offer={companyPackage}
          canOrder={canConcludeContract}
          pending={checkoutPending === companyPackage.product_id}
          onOrder={() => {
            const product = products?.find((p) => p.id === companyPackage.product_id);
            if (!product) return;
            orderReview.start({
              productId: product.id,
              seatCount: companyPackage.seat_count,
              onOrder: ({ seatCount, discountCode }) => runBuy(product, seatCount, discountCode),
            });
          }}
        />
      )}

      <nav className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800" aria-label={t('filterAria')}>
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              filter === f
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {t(`filter.${f}`)}
          </button>
        ))}
      </nav>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!products && !error && (
        <p className="text-sm text-gray-500">{t('loading')}</p>
      )}

      {products && grouped.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {t('empty')}
        </div>
      )}

      <div className="space-y-8">
        {grouped.map(([family, items]) => (
          <section key={family}>
            <SectionHeader
              family={family}
              tenantCount={tenantCount}
              activeOmsSubs={(subs ?? []).filter(
                (s) =>
                  items.some((p) => p.id === s.product_id) &&
                  family === 'oms',
              ).length}
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  activeSubscription={activeByProduct.get(product.id) ?? null}
                  checkoutPending={checkoutPending === product.id}
                  canOrder={canConcludeContract}
                  onBuy={() => onBuy(product)}
                  onManage={(sub) => onManage(sub)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {orderReview.element}
    </div>
  );
}

/**
 * OCP-1036 - the company package agreed with this account. Ordering it opens
 * the same review step as every plan; the summary there charges the package.
 */
function CompanyPackageCard({
  offer,
  canOrder,
  pending,
  onOrder,
}: {
  offer: CompanyPackageOffer;
  canOrder: boolean;
  pending: boolean;
  onOrder: () => void;
}) {
  const { t } = useTranslation('products');
  const period = offer.billing_period === 'yearly' ? t('companyPackage.periodYear') : t('companyPackage.periodMonth');
  return (
    <section
      className="mb-6 rounded-lg border border-brand-200 dark:border-brand-800 bg-brand-50/50 dark:bg-brand-900/20 p-4"
      data-testid="company-package-card"
      aria-label={t('companyPackage.title')}
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('companyPackage.title')}</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('companyPackage.intro')}</p>
      <ul className="mt-3 space-y-1 text-sm text-gray-800 dark:text-gray-200">
        <li>{t('companyPackage.seats', { count: offer.seat_count, plan: offer.product_display_name })}</li>
        <li>
          {t('companyPackage.price', {
            unit: formatMoney(offer.price_per_seat_cents, offer.currency),
            period,
            total: formatMoney(offer.total_per_period_cents, offer.currency),
          })}
        </li>
        <li>{t('companyPackage.term', { months: offer.term_months })}</li>
        {offer.includes_onboarding && <li>{t('companyPackage.includesOnboarding')}</li>}
        {offer.includes_training && <li>{t('companyPackage.includesTraining')}</li>}
        {offer.customer_note && <li className="text-gray-600 dark:text-gray-400">{offer.customer_note}</li>}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={!canOrder || pending}
          onClick={onOrder}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {t('companyPackage.order')}
        </button>
        <span className="text-xs text-gray-500">{t('companyPackage.validUntil', { date: fmtDate(offer.offer_expires_at) })}</span>
      </div>
    </section>
  );
}

/**
 * OCP-213: segment-aware section header. For the OMS family,
 * SaaS-tenant-only customers (own a Cloud tenant + no OMS sub yet)
 * get an explanatory sub-heading: "use orboto Mail in your own
 * products (separate from your tenant's included mail)" so they
 * don't confuse OMS-standalone subs with the implicit mail-sending
 * their tenant container already does internally.
 *
 * Dual-segment + OMS-standalone customers keep the plain header —
 * they already understand what an OMS sub buys.
 */
function SectionHeader({
  family,
  tenantCount,
  activeOmsSubs,
}: {
  family: string;
  tenantCount: number | null;
  activeOmsSubs: number;
}) {
  const { t } = useTranslation('products');
  const baseLabel = KNOWN_FAMILIES.has(family) ? t(`family.${family}`) : family;
  const isSaasTenantOnly =
    family === 'oms' && tenantCount !== null && tenantCount > 0 && activeOmsSubs === 0;
  return (
    <header className="mb-3">
      <h2
        className="text-sm font-semibold uppercase tracking-wide text-gray-500"
        data-testid={`section-header-${family}`}
      >
        {baseLabel}
      </h2>
      {isSaasTenantOnly && (
        <p
          className="mt-1 text-xs text-gray-600 dark:text-gray-400"
          data-testid="oms-saas-tenant-framing"
        >
          {t('omsSaasFraming')}
        </p>
      )}
    </header>
  );
}

function ProductCard({
  product,
  activeSubscription,
  checkoutPending,
  canOrder,
  onBuy,
  onManage,
}: {
  product: CatalogProduct;
  activeSubscription: ActiveSubscription | null;
  checkoutPending: boolean;
  /** OCP-570 - only owner/admin may conclude a contract; the API answers 403 otherwise. */
  canOrder: boolean;
  onBuy: () => void;
  onManage: (sub: ActiveSubscription) => void;
}) {
  const { t } = useTranslation('products');
  const isActive = activeSubscription !== null;
  // Marketing bullets are the source-of-truth when set. Otherwise
  // fall back to the full features-derived bullet list (OCP-210)
  // instead of a single auto-derived line, so customers can see what
  // they're getting before the operator hand-curates each tile.
  const bullets =
    product.marketing_bullets && product.marketing_bullets.length > 0
      ? product.marketing_bullets
      : featuresToBullets(product.features);
  const overage = overageLine(product.overage_config);

  return (
    <article className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {product.display_name}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            <Price cents={product.price_eur_cents} />{' '}
            <span className="text-xs text-gray-400">
              {KNOWN_PERIODS.has(product.billing_period) ? t(`period.${product.billing_period}`) : product.billing_period}
            </span>
          </p>
        </div>
        {isActive && (
          <span
            data-testid={`active-badge-${product.id}`}
            className="rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300"
          >
            {t('activeBadge')}
          </span>
        )}
      </header>

      {product.description && (
        <div className="prose prose-sm mt-3 max-w-none text-gray-700 dark:text-gray-300">
          <ReactMarkdown>{product.description}</ReactMarkdown>
        </div>
      )}

      {bullets.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-gray-700 dark:text-gray-300">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <span aria-hidden="true" className="text-brand-500">
                ·
              </span>
              <span className="prose prose-sm max-w-none [&_p]:m-0">
                <ReactMarkdown>{b}</ReactMarkdown>
              </span>
            </li>
          ))}
        </ul>
      )}

      {overage && (
        <p
          data-testid={`overage-line-${product.id}`}
          className="mt-3 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-2 py-1.5 text-xs text-amber-900 dark:text-amber-200"
        >
          <strong>{t('overageLabel')}</strong> {overage}.
        </p>
      )}

      <div className="mt-auto pt-4">
        {isActive ? (
          <button
            type="button"
            onClick={() => onManage(activeSubscription)}
            className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('manage')}
          </button>
        ) : (
          <button
            type="button"
            onClick={onBuy}
            disabled={checkoutPending || !canOrder}
            title={canOrder ? undefined : t('orderOnlyAdmin')}
            className="w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {/* OCP-526 - this is NOT the ordering button. It opens the review
                step; the button that places the binding offer lives in that
                dialog, directly under the summary, and carries the label the
                API serves. Keeping this one plainly navigational is what makes
                that separation honest. */}
            {checkoutPending ? t('startingCheckout') : t('reviewOrder')}
          </button>
        )}
      </div>
    </article>
  );
}

function Price({ cents }: { cents: number }) {
  // EUR formatter, integer cents → human format.
  const eur = cents / 100;
  const formatted = new Intl.NumberFormat(intlTag(), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(eur);
  return <span className="text-base font-medium text-gray-900 dark:text-gray-100">{formatted}</span>;
}

